import Cookies from "js-cookie";
import React, { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Route, RouteComponentProps, RouteProps, withRouter } from "react-router-dom";
import { fetchCurrentUserDetails, fetchCXOToken, setCanAccessView } from "../../../actions";
import { getLastDataRefresh } from "../../../api";
import { getFlashMessage, getReleaseNotesData } from "../../../api/releaseNotesApi";
import { PUBLIC_AUTHENTICATED_PATHS } from "../../../constants";
import { IReleaseNote } from "../../../constants/interface/admin/releaseNotes";
import { PopUpMessagesContext } from "../../../context";
import { AccessDeniedPage } from "../../../pages/Error Pages/Login";
import {
  evaluateLegacyAccessControl,
  evaluateNewAccessControl,
  logAccessControlDivergence,
  NEW_ACCESS_CONTROL_IS_AUTHORITATIVE,
  parseViewsCookie,
} from "./accessControl";

const isUserAuthenticated = () => !!Cookies.get("access_token");

interface Props extends RouteProps {
  search?: string;
}

export const PrivateRoute = withRouter((props: Props & RouteComponentProps) => {
  const { path, history, location, ...rest } = props;
  const { setFlashMessage, setShowFlashMessage, setReleaseNote, setShowReleaseMessage } = useContext(PopUpMessagesContext);

  const dispatch = useDispatch();

  const [isAuthenticated, setIsAuthentication] = useState<boolean>(true);
  const [canAccessRoute, setCanAccessRoute] = useState<boolean>(true);

  const { search } = location;

  const updatedPath = search ? path + search : path;

  useEffect(() => {
    setIsAuthentication(isUserAuthenticated());
  }, []);

  // Redirects user to login page if not authenticated
  useEffect(() => {
    if (!isAuthenticated || !Cookies.get("views")) {
      history.push({ pathname: "/login", search: `?redirect=${updatedPath}` });
    } else {
      Promise.all([
        //Fetch flash message
        getFlashMessage().then((response) => {
          if (response && localStorage.getItem("latestFlashMsgSeen") === "false") {
            setFlashMessage(response as unknown as string);
            setShowFlashMessage(true);
          }
        }),
        // Fetch release notes
        getReleaseNotesData().then((response) => {
          if (response && response.valid && !response.seen) {
            const notesValue: string = response.popup_note;
            setReleaseNote(response as IReleaseNote);
            setShowReleaseMessage(true);
          }
        }),
        dispatch(fetchCurrentUserDetails()),
        dispatch(fetchCXOToken()),
      ]);
    }
  }, [isAuthenticated]);

  //HACK to logout users whenever an endpoint throws an error.
  //The function will run every 15 minutes to check if a user is logged in by doing an api call and checking the response status
  useEffect(() => {
    const authenticateInterval = setInterval(() => {
      getLastDataRefresh("digital_performance").then((response: any) => {
        if (response && "error" in response) {
          const responseStatus = response.error?.response?.status;
          if (responseStatus === 403) {
            setIsAuthentication(false);
          }
        }
      });
    }, 900000);
    return () => clearInterval(authenticateInterval);
  }, []);

  useEffect(() => {
    const currentPath = location.pathname;

    // General shell pages (favourites/dashboards/analytics/settings/faq/alerts/archived) - any
    // authenticated user should reach these regardless of dashboard-specific views. Immediate and
    // authoritative, never part of the old/new shadow comparison below (there's no meaningful
    // "legacy" behavior to diff against - these paths were previously ungated <Route>s).
    if (PUBLIC_AUTHENTICATED_PATHS.includes(currentPath)) {
      setCanAccessRoute(isAuthenticated);
      return;
    }

    const views = parseViewsCookie(Cookies.get("views"));

    const oldResult = evaluateLegacyAccessControl(currentPath, views);
    const newResult = evaluateNewAccessControl(currentPath, views);

    if (oldResult !== newResult.allowed) {
      logAccessControlDivergence(currentPath, oldResult, newResult, views);
    }

    setCanAccessRoute(NEW_ACCESS_CONTROL_IS_AUTHORITATIVE ? newResult.allowed : oldResult);
  }, [location.pathname, isAuthenticated]);

  // reset flag that checks whether a user can access a page
  useEffect(() => {
    dispatch(setCanAccessView(true));
  }, [history.location]);

  return canAccessRoute ? <Route {...rest} path={updatedPath} /> : <AccessDeniedPage />;
});
